import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Inbox, FileDown, FilePlus2, Trash2, Loader2, Mail, Phone, MapPin, Camera } from "lucide-react";
import { projectsToSections } from "../lib/publicPlan";

function InquiryPhotos({ inq }) {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    let alive = true;
    const created = [];
    (inq.photos || []).forEach((ph) => {
      api.get(`/inquiries/${inq.id}/photos/${ph.id}`, { responseType: "blob" }).then((r) => {
        if (!alive) return;
        const u = URL.createObjectURL(r.data); created.push(u);
        setUrls((p) => ({ ...p, [ph.id]: u }));
      }).catch(() => {});
    });
    return () => { alive = false; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [inq.id, inq.photos]);
  if (!inq.photos?.length) return null;
  return (
    <div className="mt-3" data-testid={`inquiry-photos-${inq.number}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Camera size={11}/> {inq.photos.length} Datei(en)</div>
      <div className="flex flex-wrap gap-2">
        {inq.photos.map((ph) => (
          <a key={ph.id} href={urls[ph.id] || "#"} target="_blank" rel="noreferrer" className="w-24 h-24 border border-zinc-200 bg-zinc-50 block overflow-hidden" title={ph.filename} data-testid="inquiry-photo">
            {urls[ph.id] ? ((ph.filename || "").toLowerCase().endsWith(".pdf") ? <div className="w-full h-full flex flex-col items-center justify-center text-[9px] text-zinc-700 p-1 text-center break-all">PDF<br/>{ph.filename}</div> : <img src={urls[ph.id]} alt={ph.filename} className="w-full h-full object-cover hover:scale-105 transition-transform" />) : <div className="w-full h-full flex items-center justify-center"><Loader2 size={14} className="animate-spin text-zinc-400"/></div>}
          </a>
        ))}
      </div>
    </div>
  );
}

const STATUS = {
  neu: { label: "Neu", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  in_bearbeitung: { label: "In Bearbeitung", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  angebot: { label: "Angebot erstellt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  erledigt: { label: "Erledigt", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Inquiries() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/inquiries").then((r) => setItems(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);

  const setStatus = async (inq, status) => {
    const r = await api.patch(`/inquiries/${inq.id}`, { status });
    setItems((p) => p.map((x) => (x.id === inq.id ? r.data : x)));
  };
  const remove = async (inq) => {
    if (!window.confirm(`Anfrage ${inq.number} löschen?`)) return;
    await api.delete(`/inquiries/${inq.id}`);
    setItems((p) => p.filter((x) => x.id !== inq.id));
  };
  const createQuote = async (inq) => {
    setBusy(inq.id);
    try {
      const full = (await api.get(`/inquiries/${inq.id}`)).data;
      const c = full.contact || {};
      const { sketch, ...rawPlan } = { ...full.plan };
      // Alle Abschnitte (Projekte) der Kundenanfrage -> interne Planer-Sections
      const plan = rawPlan.projects?.length ? { sections: projectsToSections(rawPlan), active: 0, view: "grundriss" } : rawPlan;
      // Kundenname gehört in "Firma / Name, Nachname"; Ansprechpartner nur bei echter Firma
      const primary = c.company || c.name || "";
      const contactPerson = c.company ? (c.name || "") : "";
      navigate("/planner", { state: { plan, inquiry: { id: full.id, number: full.number, customer: {
        name: contactPerson, company: primary, email: c.email || "", phone: c.phone || "", zipCode: c.zip || "", city: c.city || "", country: c.country || "Deutschland", address: "",
      }, projectName: `Zaunplan-Anfrage ${full.number}`, ral: rawPlan.ral, message: c.message } } });
    } finally { setBusy(""); }
  };
  const pdf = async (inq) => {
    const r = await api.get(`/inquiries/${inq.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid="inquiries-page">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Online-Zaunplaner</div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight flex items-center gap-2"><Inbox size={22}/> Anfragen</h1>
        </div>
        <a href="/zaunplaner" target="_blank" rel="noreferrer" className="text-xs underline text-zinc-600 hover:text-zinc-900" data-testid="open-public-planner">Öffentlichen Zaunplaner öffnen</a>
      </div>
      {error && <div className="text-sm text-red-600 mb-4">{error}</div>}
      {!items ? <Loader2 className="animate-spin"/> : items.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-10 text-center" data-testid="inquiries-empty">Noch keine Anfragen. Kunden können unter <span className="font-mono">/zaunplaner</span> ihren Zaun planen und anfragen.</div>
      ) : (
        <div className="space-y-3">
          {items.map((inq) => {
            const c = inq.contact || {}, p = inq.plan || {}, st = STATUS[inq.status] || STATUS.neu;
            return (
              <div key={inq.id} className="bg-white border border-zinc-200 p-4 md:p-5" data-testid={`inquiry-${inq.number}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-zinc-900">{inq.number}</span>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${st.cls}`} data-testid={`inquiry-status-${inq.number}`}>{st.label}</span>
                      <span className="text-xs text-zinc-500">{fmt(inq.createdAt)}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-zinc-900">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-zinc-600 mt-0.5">
                      <span className="flex items-center gap-1"><Mail size={11}/> {c.email}</span>
                      {c.phone && <span className="flex items-center gap-1"><Phone size={11}/> {c.phone}</span>}
                      {(c.zip || c.city) && <span className="flex items-center gap-1"><MapPin size={11}/> {c.zip} {c.city}{c.country ? `, ${c.country}` : ""}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-700 text-right">
                    <div><b>{p.model}</b>{p.variant ? ` · ${p.variant}` : ""} · H {p.height} mm</div>
                    <div>{p.ral || "—"}</div>
                    <div>{(p.points || []).length >= 2 ? `${Math.max(0, (p.points || []).length - (p.closed ? 0 : 1))} Segmente` : ""} · {(p.gates || []).length} Tor(e)</div>
                    {(p.projectSummaries || []).length > 0 && <ul className="mt-1 space-y-0.5 text-[11px] text-zinc-700" data-testid={`inquiry-projects-${inq.number}`}>{p.projectSummaries.map((t, i) => <li key={i} className="border-l-2 border-zinc-300 pl-1.5">{t}</li>)}</ul>}
                  </div>
                </div>
                {c.message && <div className="mt-2 text-sm text-zinc-700 bg-zinc-50 border border-zinc-100 px-3 py-2 whitespace-pre-wrap">{c.message}</div>}
                <InquiryPhotos inq={inq} />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select value={inq.status} onChange={(e) => setStatus(inq, e.target.value)} className="text-xs px-2 py-1.5 border border-zinc-300 rounded-sm bg-white" data-testid={`inquiry-status-select-${inq.number}`}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <button onClick={() => pdf(inq)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 rounded-sm hover:border-zinc-900" data-testid={`inquiry-pdf-${inq.number}`}><FileDown size={12}/> Skizze PDF</button>
                  {inq.quoteId ? (
                    <button onClick={() => navigate(`/quote/${inq.quoteId}`)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-emerald-600 text-emerald-700 rounded-sm hover:bg-emerald-50" data-testid={`inquiry-open-quote-${inq.number}`}>Angebot öffnen</button>
                  ) : (
                    <button onClick={() => createQuote(inq)} disabled={busy === inq.id} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50" data-testid={`inquiry-create-quote-${inq.number}`}>
                      {busy === inq.id ? <Loader2 size={12} className="animate-spin"/> : <FilePlus2 size={12}/>} Angebot erstellen
                    </button>
                  )}
                  <div className="flex-1"/>
                  <button onClick={() => remove(inq)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`inquiry-delete-${inq.number}`}><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
